import { useState, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/features/common/components/UserAvatar";
import { cn } from "@/lib/utils";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

/**
 * Sales Team Section Widget
 * 
 * Displays sales team members with rankings, commission details, and progress indicators.
 * Apple-style minimal design with enhanced UX features.
 */
export function SalesTeamSection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  isSaving,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { language } = useLanguage();
  

  // Resolve UI labels
  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => {
      return getUITranslation(key, language) || fallback;
    };

    return {
      salesTeamFieldMissing: getUI('sales_team_field_missing', 'Sales Team field missing'),
      noSalesTeamEntries: getUI('no_sales_team_entries', 'No sales team entries'),
      orderTotal: getUI('order_total', 'Order Total'),
      commissionPool: getUI('commission_pool', 'Commission Pool'),
      ofOrder: getUI('of_order', 'of order'),
      teamMembers: getUI('team_members', 'Team Members'),
      person: getUI('person', 'person'),
      people: getUI('people', 'people'),
      avg: getUI('avg', 'avg'),
      each: getUI('each', 'each'),
      topEarner: getUI('top_earner', 'Top Earner'),
      commissionEarned: getUI('commission_earned', 'Commission Earned'),
      handlesPercentOfOrder: getUI('handles_percent_of_order', 'Handles {percentage}% of this order'),
      salesShare: getUI('sales_share', 'Sales Share'),
      of: getUI('of', 'of'),
      commissionRate: getUI('commission_rate', 'Commission Rate'),
      baseRate: getUI('base_rate', 'base rate'),
      effectiveRate: getUI('effective_rate', 'Effective Rate'),
      actualRate: getUI('actual_rate', 'actual rate'),
      totalShare: getUI('total_share', 'Total Share'),
      totalSalesAmount: getUI('total_sales_amount', 'Total Sales Amount'),
      totalCommissionEarned: getUI('total_commission_earned', 'Total Commission Earned'),
    };
  }, [language]);

  // Find the sales team table field
  const allFields = useMemo(
    () => section.columns.flatMap((col) => col.fields),
    [section.columns]
  );

  const field = allFields.find(
    (f) => f.fieldtype === 'Table' || f.fieldtype === 'Table MultiSelect'
  );

  const rows: any[] = field && Array.isArray(formData[field.fieldname])
    ? formData[field.fieldname]
    : [];

  const fmt = (v: any) => (v === null || v === undefined || v === "" ? "—" : v);
  const currency = formData.currency || 'THB';
  const fmtCurrency = (v: any) => {
    if (v === null || v === undefined || v === "") return "—";
    const num = typeof v === 'string' ? parseFloat(v) : Number(v);
    if (isNaN(num)) return "—";
    return `${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${currency})`;
  };

  // Order-level context calculations
  const orderTotal = Number(formData.grand_total || formData.net_total || 0);
  const totalContributionPct = rows.reduce((acc, r) => acc + (Number(r.allocated_percentage) || 0), 0);
  const totalAllocatedAmount = rows.reduce((acc, r) => acc + (Number(r.allocated_amount) || 0), 0);
  const totalIncentives = rows.reduce((acc, r) => acc + (Number(r.incentives) || 0), 0);
  const avgCommission = rows.length > 0 ? totalIncentives / rows.length : 0;
  const commissionPoolPct = orderTotal > 0 ? (totalIncentives / orderTotal) * 100 : 0;

  // Sort by commission earned (highest first) for ranking
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aIncentive = Number(a.incentives || 0);
      const bIncentive = Number(b.incentives || 0);
      return bIncentive - aIncentive;
    });
  }, [rows]);

  // Calculate effective commission rate for each row
  const getEffectiveRate = (row: any) => {
    const allocated = Number(row.allocated_amount || 0);
    const incentive = Number(row.incentives || 0);
    if (allocated > 0) {
      return ((incentive / allocated) * 100).toFixed(2);
    }
    return "0.00";
  };

  return (
    <>
      <div className="space-y-2">
        {!field ? (
          <div className={CARD}>
            <div className="p-5">
              <div className="text-sm text-red-500">{uiLabels.salesTeamFieldMissing}</div>
            </div>
          </div>
        ) : !rows || rows.length === 0 ? (
          <div className={CARD}>
            <div className="p-5">
              <div className="text-sm text-muted-foreground">{uiLabels.noSalesTeamEntries}</div>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              CARD,
              !disabled && "cursor-pointer",
              disabled && "cursor-not-allowed"
            )}
            onClick={() => !disabled && setModalOpen(true)}
          >
            <div className="p-8 sm:p-10">
              <div className="space-y-8">
                {/* Order Context Header - Apple style minimal */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pb-8 border-b border-border/20">
                  <div className="flex flex-col space-y-1.5">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.orderTotal}</span>
                    <span className="text-base font-medium text-foreground leading-tight">{fmtCurrency(orderTotal)}</span>
                  </div>
                  <div className="flex flex-col space-y-1.5">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.commissionPool}</span>
                    <div className="flex flex-col">
                      <span className="text-base font-medium text-foreground leading-tight">{fmtCurrency(totalIncentives)}</span>
                      {orderTotal > 0 && (
                        <span className="text-xs text-muted-foreground mt-0.5 font-normal">
                          {commissionPoolPct.toFixed(1)}% {uiLabels.ofOrder}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col space-y-1.5">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.teamMembers}</span>
                    <div className="flex flex-col">
                      <span className="text-base font-medium text-foreground leading-tight">
                        {rows.length} {rows.length === 1 ? uiLabels.person : uiLabels.people}
                      </span>
                      {rows.length > 0 && (
                        <span className="text-xs text-muted-foreground mt-0.5 font-normal">
                          {uiLabels.avg} {fmtCurrency(avgCommission)} {uiLabels.each}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sales Team Cards - Minimal Apple style */}
                <div className="space-y-6">
                  {sortedRows.map((r, idx) => {
                    const rank = idx + 1;
                    const allocatedPct = Number(r.allocated_percentage || 0);
                    const allocatedAmount = Number(r.allocated_amount || 0);
                    const incentive = Number(r.incentives || 0);
                    const effectiveRate = getEffectiveRate(r);
                    const commissionPctOfOrder = orderTotal > 0 ? ((incentive / orderTotal) * 100).toFixed(1) : "0.0";
                    const isTopPerformer = rank === 1;
                    const salesPersonName = fmt(r.sales_person);

                    return (
                      <div
                        key={r.name || idx}
                        className="rounded-md px-6 py-6 bg-[rgb(247_249_253_/_91%)] dark:bg-gray-800/60 border-0 hover:bg-[rgb(240_243_248_/_95%)] dark:hover:bg-gray-800/80 transition-all duration-300 ease-out"
                      >
                        <div className="flex flex-col space-y-6">
                          {/* Top Row: Avatar, Name, Rank Badge, Commission */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <UserAvatar
                                fallbackText={salesPersonName}
                                size="lg"
                                className="flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                  <div className="text-base font-medium text-foreground leading-tight truncate">
                                    {salesPersonName}
                                  </div>
                                  {isTopPerformer && (
                                    <Badge variant="outline" className="text-[10px] px-2.5 py-0.5 h-5 border-primary/30 text-primary bg-primary/5">
                                      {uiLabels.topEarner}
                                    </Badge>
                                  )}
                                  {rank > 1 && !isTopPerformer && (
                                    <Badge variant="outline" className="text-[10px] px-2.5 py-0.5 h-5 text-muted-foreground border-border/40">
                                      #{rank}
                                    </Badge>
                                  )}
                                </div>
                                {r.contact_no && (
                                  <div className="text-xs text-muted-foreground font-normal">
                                    {r.contact_no}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end flex-shrink-0 space-y-1">
                              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.commissionEarned}</span>
                              <span className="text-2xl font-semibold text-foreground leading-tight">{fmtCurrency(r.incentives)}</span>
                              {orderTotal > 0 && (
                                <span className="text-[10px] text-muted-foreground mt-1 font-normal">
                                  {commissionPctOfOrder}% {uiLabels.ofOrder}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Progress Bar for Sales Share - Apple blue gradient */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground font-normal">
                                {uiLabels.handlesPercentOfOrder.replace('{percentage}', allocatedPct.toString())}
                              </span>
                              <span className="font-medium text-foreground">{fmtCurrency(allocatedAmount)}</span>
                            </div>
                            <div className="w-full h-2 bg-blue-50/50 dark:bg-blue-900/15 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 dark:from-blue-500 dark:via-blue-600 dark:to-blue-700"
                                style={{ width: `${Math.min(allocatedPct, 100)}%` }}
                              />
                            </div>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-3 gap-8 pt-5 border-t border-border/15">
                            <div className="flex flex-col space-y-1.5">
                              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.salesShare}</span>
                              <span className="text-base font-medium text-foreground leading-tight">{fmt(r.allocated_percentage)}%</span>
                              <span className="text-[10px] text-muted-foreground mt-0.5 font-normal">
                                {uiLabels.of} {fmtCurrency(orderTotal)}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-1.5">
                              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.commissionRate}</span>
                              <span className="text-base font-medium text-foreground leading-tight">{fmt(r.commission_rate)}%</span>
                              <span className="text-[10px] text-muted-foreground mt-0.5 font-normal">
                                {uiLabels.baseRate}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-1.5">
                              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.effectiveRate}</span>
                              <span className="text-base font-medium text-foreground leading-tight">{effectiveRate}%</span>
                              <span className="text-[10px] text-muted-foreground mt-0.5 font-normal">
                                {uiLabels.actualRate}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Totals Summary - Minimal */}
                <div className="pt-8 border-t border-border/20 bg-gray-50/30 dark:bg-gray-800/20 -mx-8 sm:-mx-10 px-8 sm:px-10 py-6 rounded-b-md">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                    <div className="flex flex-col space-y-1.5">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.totalShare}</span>
                      <span className="text-base font-medium text-foreground">{fmt(totalContributionPct)}%</span>
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.totalSalesAmount}</span>
                      <span className="text-base font-medium text-foreground">{fmtCurrency(totalAllocatedAmount)}</span>
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{uiLabels.totalCommissionEarned}</span>
                      <span className="text-xl font-medium text-foreground">{fmtCurrency(totalIncentives)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
      />
    </>
  );
}

